// @ts-nocheck
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';

interface ColumnDef {
	columnName: string;
	displayName: string;
	type?: string;
}

interface DynamicFilterBoxProps {
	title: string;
	icon: string;
	accentColor: string;
	columns: ColumnDef[];
	gridData: any[];
	activeKeys: string[];
	onToggleKey: (key: string) => void;
	filterValues: Record<string, string>;
	onValueChange: (key: string, val: string) => void;
	matchLabel: string;
	onApplyFilter?: (columnName: string, value: string, column: ColumnDef) => void;
	defaultOpen?: boolean;
}

/* ─── Searchable dropdown ─── */
const SearchSelect = ({
	options,
	value,
	onChange,
	accentColor,
}: {
	options: string[];
	value: string;
	onChange: (v: string) => void;
	accentColor: string;
}) => {
	const [search, setSearch] = useState('');
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	const filtered = useMemo(() => {
		if (!search) return options;
		const s = search.toLowerCase();
		return options.filter((o) => o.toLowerCase().includes(s));
	}, [options, search]);

	useEffect(() => {
		const onClick = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener('mousedown', onClick);
		return () => document.removeEventListener('mousedown', onClick);
	}, []);

	const displayVal = value === 'all' ? `All (${options.length})` : value;

	return (
		<div className="obs-ss" ref={ref}>
			<div
				className="obs-ss__trigger"
				onClick={() => setOpen(!open)}
				style={{ color: value === 'all' ? '#aaa' : '#333' }}
			>
				<span className="obs-ss__val">{displayVal}</span>
				<span className="obs-ss__arrow">▾</span>
			</div>
			{open && (
				<div className="obs-ss__drop">
					<input
						className="obs-ss__search"
						type="text"
						placeholder="Search..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						autoFocus
						onClick={(e) => e.stopPropagation()}
					/>
					<div className="obs-ss__list">
						<div
							className="obs-ss__opt obs-ss__opt--all"
							onClick={() => {
								onChange('all');
								setOpen(false);
								setSearch('');
							}}
						>
							All ({options.length})
						</div>
						{filtered.map((o) => (
							<div
								key={o}
								className={`obs-ss__opt ${o === value ? 'obs-ss__opt--sel' : ''}`}
								onClick={() => {
									onChange(o);
									setOpen(false);
									setSearch('');
								}}
							>
								{o}
							</div>
						))}
						{filtered.length === 0 && <div className="obs-ss__empty">No matches</div>}
					</div>
				</div>
			)}
		</div>
	);
};

const DynamicFilterBox: React.FC<DynamicFilterBoxProps> = ({
	title: boxTitle,
	icon,
	accentColor,
	columns,
	gridData,
	activeKeys,
	onToggleKey,
	filterValues,
	onValueChange,
	matchLabel,
	onApplyFilter,
	defaultOpen = false,
}) => {
	const [isOpen, setIsOpen] = useState(defaultOpen);

	useEffect(() => {
		if (activeKeys.length > 0 && !isOpen) setIsOpen(true);
	}, [activeKeys.length]);

	const availableColumns = useMemo(() => {
		if (!columns.length) return [];
		if (!gridData || !gridData.length) return columns;
		const gridKeys = new Set(Object.keys(gridData[0] || {}));
		const matched = columns.filter((c) => gridKeys.has(c.columnName));
		return matched.length > 0 ? matched : columns;
	}, [columns, gridData]);

	const distinctValues = useMemo(() => {
		const result: Record<string, string[]> = {};
		if (!gridData || !gridData.length) return result;
		activeKeys.forEach((key) => {
			const values = gridData
				.map((row) => row[key])
				.filter((v) => v !== null && v !== undefined && v !== '')
				.map((v) => String(v))
				.filter((v, i, arr) => arr.indexOf(v) === i)
				.sort((a, b) => {
					const na = Number(a),
						nb = Number(b);
					if (!isNaN(na) && !isNaN(nb)) return na - nb;
					return a.localeCompare(b);
				});
			result[key] = values;
		});
		return result;
	}, [gridData, activeKeys]);

	const inactiveColumns = availableColumns.filter((c) => !activeKeys.includes(c.columnName));
	const activeCount = activeKeys.filter((k) => filterValues[k] && filterValues[k] !== 'all').length;

	const handleValueChange = (key: string, val: string) => {
		onValueChange(key, val);
		if (onApplyFilter) {
			const col = columns.find((c) => c.columnName === key);
			if (col) onApplyFilter(key, val, col);
		}
	};

	return (
		<div className="obs-filter-box">
			<div className="obs-filter-box__header" style={{ color: accentColor }} onClick={() => setIsOpen(!isOpen)}>
				<span className="obs-filter-box__header-left">
					<span className={`obs-filter-box__chevron ${isOpen ? 'obs-filter-box__chevron--open' : ''}`}>
						▶
					</span>
					{icon} {boxTitle}
				</span>
				<span className="obs-filter-box__count">{activeCount > 0 ? `${activeCount} active` : ''}</span>
			</div>

			{isOpen && (
				<div className="obs-filter-box__body">
					{activeKeys.map((key) => {
						const col = availableColumns.find((c) => c.columnName === key);
						if (!col) return null;
						const options = distinctValues[key] || [];
						return (
							<div key={key} className="obs-filter-box__row">
								<div className="obs-filter-box__field">
									<div className="obs-filter-box__label">{col.displayName || col.columnName}</div>
									<SearchSelect
										options={options}
										value={filterValues[key] || 'all'}
										onChange={(v) => handleValueChange(key, v)}
										accentColor={accentColor}
									/>
								</div>
								<button
									onClick={() => onToggleKey(key)}
									title="Remove filter"
									className="obs-filter-box__remove-btn"
								>
									✕
								</button>
							</div>
						);
					})}
					{inactiveColumns.length > 0 && (
						<div style={{ marginTop: activeKeys.length > 0 ? 6 : 0 }}>
							<div className="obs-filter-box__add-label">+ ADD FILTER COLUMN</div>
							<div className="obs-filter-box__chips">
								{inactiveColumns.map((col) => (
									<button
										key={col.columnName}
										onClick={() => onToggleKey(col.columnName)}
										className="obs-filter-box__chip"
										style={{ borderColor: accentColor, color: accentColor }}
									>
										+ {col.displayName || col.columnName}
									</button>
								))}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default DynamicFilterBox;
